import { useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import {Menu, X, Shield, ChevronRight, LogIn, User, Settings} from 'lucide-react'
import { useAuth } from "@/hooks/useAuth"

const navLinks = [
  { label: "Accueil", path: "/" },
  { label: "Services", path: "/services" },
  { label: "Support", path: "/support" },
  { label: "A propos", path: "/a-propos" },
  { label: "Contact", path: "/contact" },
]

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()
  const { isAuthenticated, isAdmin } = useAuth()

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-slate-950/90 backdrop-blur-xl border-b border-slate-800/50 shadow-lg shadow-slate-950/50"
          : "bg-transparent"
      }`}
    >
      <div className="section-padding">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <img 
              src="https://static.lumi.new/3d/3d3904167daeab807172c4efcb0502aa.webp" 
              alt="LJJ IT Logo" 
              className="h-12 sm:h-14 lg:h-16 w-auto object-contain bg-white rounded-lg px-2 py-1 group-hover:scale-105 transition-transform duration-300"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? "text-cyan-400 bg-cyan-500/10"
                      : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/50"
                  }`}
                >
                  {link.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-cyan-400 rounded-full" />
                  )}
                </Link>
              )
            })}
          </div>

          {/* CTA + Mobile toggle */}
          <div className="flex items-center gap-3">
            {isAuthenticated ? (
              <>
                {isAdmin && (
                  <Link to="/admin" className="hidden md:inline-flex btn-secondary text-sm !py-2.5 !px-4">
                    <Settings className="w-4 h-4" />
                    Admin
                  </Link>
                )}
                <Link to="/espace-client" className="hidden md:inline-flex btn-ghost text-sm !py-2.5 !px-4">
                  <User className="w-4 h-4" />
                  Mon espace
                </Link>
              </>
            ) : (
              <Link to="/auth" className="hidden md:inline-flex btn-ghost text-sm !py-2.5 !px-4">
                <LogIn className="w-4 h-4" />
                Connexion
              </Link>
            )}
            <Link to="/rendez-vous" className="hidden sm:inline-flex btn-primary text-sm !py-2.5 !px-5">
              Prendre rendez-vous
              <ChevronRight className="w-4 h-4" />
            </Link>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-slate-400 hover:text-white transition-colors"
              aria-label="Menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 ${
          isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <div className="section-padding pb-6 pt-2 bg-slate-950/95 backdrop-blur-xl border-t border-slate-800/50">
          <div className="flex flex-col gap-1">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? "text-cyan-400 bg-cyan-500/10"
                      : "text-slate-400 hover:text-white hover:bg-slate-800/50"
                  }`}
                >
                  {link.label}
                </Link>
              )
            })}
            {isAuthenticated ? (
              <>
                <Link to="/espace-client" className="btn-ghost text-sm mt-2 justify-center">
                  <User className="w-4 h-4" />
                  Mon espace
                </Link>
                {isAdmin && (
                  <Link to="/admin" className="btn-secondary text-sm mt-2 justify-center">
                    <Settings className="w-4 h-4" />
                    Admin
                  </Link>
                )}
              </>
            ) : (
              <Link to="/auth" className="btn-ghost text-sm mt-2 justify-center">
                <LogIn className="w-4 h-4" />
                Connexion
              </Link>
            )}
            <Link to="/rendez-vous" className="btn-primary text-sm mt-2 justify-center">
              Prendre rendez-vous
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </nav>
  )
}
